import { Box, Flex, Icon, Text } from "@chakra-ui/react";
import type { NavItem } from "./navConfig";
import { useNavLabel } from "./useNavLabel";

interface Props {
  item: NavItem;
  isActive: boolean;
  badge?: number;
  onClick: () => void;
  /**
   * Renders with the same weight as a section header row. For sections that collapse into a single
   * plain row (Overview, Assistant) — two rows on the same level must not look like two levels.
   */
  topLevel?: boolean;
}

export const NavBadgeCount = ({ value, isActive }: { value: number; isActive?: boolean }) => (
  <Box
    flexShrink={0}
    minW="20px"
    h="20px"
    px="1.5"
    rounded="full"
    fontSize="11px"
    fontWeight="semibold"
    lineHeight="20px"
    textAlign="center"
    bg={isActive ? "smPrimary" : "smBackgroundTertiary"}
    color={isActive ? "white" : "smText"}
  >
    {value}
  </Box>
);

export const SidebarNavItem = ({ item, isActive, badge, onClick, topLevel }: Props) => {
  const navLabel = useNavLabel();

  return (
    <Flex
      as="button"
      type="button"
      onClick={onClick}
      align="center"
      gap="3"
      w="full"
      // Indented under its section header, so a submenu row reads as one level down. A section that
      // renders as a single plain row is not under anything and keeps the header's own inset.
      pl={topLevel ? "3" : "8"}
      pr="3"
      py="2"
      mb="0.5"
      rounded="lg"
      textAlign="left"
      transition="background-color 0.15s, color 0.15s"
      bg={isActive ? "smPrimarySofter" : "transparent"}
      color={isActive ? "smPrimary" : topLevel ? "smText" : "smMuted"}
      fontWeight={isActive || topLevel ? "medium" : "normal"}
      fontSize="sm"
      _hover={{ bg: isActive ? "smPrimarySofter" : "smBackgroundSecondary" }}
      // Chakra's Collapse hard-codes overflow:hidden inline on the submenu, and the aside clips
      // too, so a ring drawn outside the row would be cut on every side. This one is inside it.
      _focusVisible={{
        boxShadow: "inset 0 0 0 2px var(--chakra-colors-smPrimary)",
        outline: "none",
      }}
    >
      <Icon as={item.icon} boxSize="18px" flexShrink={0} />
      <Text flex="1" isTruncated title={navLabel(item.labelKey)}>
        {navLabel(item.labelKey)}
      </Text>
      {badge != null && badge > 0 && <NavBadgeCount value={badge} isActive={isActive} />}
    </Flex>
  );
};
